import React from "react";
import { Formik, Form } from "formik";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import * as Yup from "yup";
import CustomButton from "../common/button/CustomButton";
import theme from "../../theme";
import CustomInput from "../common/input/CustomInput";
import {
  StyledAddressFieldGrid,
  StyledButtonGroupWrapper,
  StyledTypography,
} from "../address-default/style";
import { useAddressLocationData } from "../../hooks/useAddressLocationData";
import CountryStateFields from "../common/country-state-fields/CountryStateFields";
import { normalizeAddressFields } from "../../utils/orderAddress";

const validationSchema = Yup.object({
  street_address: Yup.string().required("Street Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  country: Yup.string().required("Country is required"),
  zip_code: Yup.string().required("Zip code is required"),
});

/**
 * Edits delivery address for an order only — does not call profile /address APIs.
 */
export default function OrderDeliveryAddressForm({
  initialValues,
  onSave,
  onCancel,
  embedded = true,
  submitLabel = "Save address",
}) {
  const {
    countries,
    loadingCountries,
    getStatesForCountry,
    getCitiesForCountryState,
  } = useAddressLocationData();

  const formInitial = normalizeAddressFields(initialValues);

  return (
    <Box
      p={embedded ? 0 : 3}
      borderRadius={embedded ? 0 : 2}
      bgcolor={embedded ? "transparent" : "#f1f8ff"}
      border={embedded ? "none" : "1px solid #cce0ff"}
      sx={embedded ? { minWidth: 0 } : undefined}
    >
      <Formik
        initialValues={formInitial}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSave(normalizeAddressFields(values));
        }}
        enableReinitialize
      >
        {({ errors, touched, handleChange, handleBlur, values, setFieldValue }) => (
          <Form>
            <Box display="flex" alignItems="center" gap="12px" mb={2}>
              <Box
                display="flex"
                sx={{
                  background: "#EAEAEA",
                  borderRadius: "100px",
                  padding: "3px",
                  gap: "2px",
                }}
              >
                {["home", "work"].map((type) => (
                  <StyledTypography
                    key={type}
                    sx={{
                      cursor: "pointer",
                      background:
                        values.address_type === type
                          ? theme.palette.custom.primary
                          : "transparent",
                      color:
                        values.address_type === type
                          ? theme.palette.custom.white
                          : theme.palette.custom.lockerValueColor,
                      fontWeight:
                        values.address_type === type
                          ? theme.typography.fontWeight.medium
                          : theme.typography.fontWeight.regular,
                      transition: "all 0.2s ease",
                      width: "80px",
                    }}
                    onClick={() => setFieldValue("address_type", type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </StyledTypography>
                ))}
              </Box>
            </Box>
            <Grid container spacing={2}>
              <StyledAddressFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                <CustomInput
                  required
                  formGrid={embedded}
                  customLabel="Street Address"
                  name="street_address"
                  value={values.street_address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.street_address && Boolean(errors.street_address)
                  }
                  helperText={touched.street_address && errors.street_address}
                />
              </StyledAddressFieldGrid>
              <CountryStateFields
                fieldPrefix=""
                hideCountry
                countries={countries}
                loadingCountries={loadingCountries}
                getStatesForCountry={getStatesForCountry}
                getCitiesForCountryState={getCitiesForCountryState}
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                handleBlur={handleBlur}
                showZip
                zipPath="zip_code"
                formGrid={embedded}
                muiGridRow
              />
            </Grid>
            <StyledButtonGroupWrapper
              style={
                embedded
                  ? { marginTop: 16, justifyContent: "flex-end" }
                  : undefined
              }
            >
              <CustomButton
                fontSize={theme.typography.base.fontSize}
                height={embedded ? "44px" : "54px"}
                btnLabel="Cancel"
                btntype="white"
                color={theme.palette.custom.lockerValueColor}
                fontWeight={theme.typography.fontWeight.medium}
                border={theme.borders.defaultButton}
                onClick={onCancel}
              />
              <CustomButton
                fontSize={theme.typography.base.fontSize}
                height={embedded ? "44px" : "54px"}
                type="submit"
                btnLabel={submitLabel}
                btntype="orderCards"
                color={theme.palette.custom.white}
                fontWeight={theme.typography.fontWeight.medium}
                border={theme.borders.defaultButton}
              />
            </StyledButtonGroupWrapper>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
